import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  DateRangePicker,
  DateRangeValidationError,
  InputLabel,
  PickerChangeHandlerContext,
  PickersShortcutsItemContext,
} from '@hopskipdrive/react-components';
import { endOfISOWeek, endOfMonth, startOfISOWeek, startOfMonth, subDays, subMonths } from 'date-fns';

import { useReportsQuery } from '../../../hooks/useReportsQuery';

interface IPickersShortcutsItemContext extends PickersShortcutsItemContext {
  value?: string;
}

interface IPickerChangeHandlerContext<TError> extends Omit<PickerChangeHandlerContext<TError>, 'shortcuts'> {
  shortcut?: IPickersShortcutsItemContext;
}

export const DateRange = () => {
  const {
    control,
    setValue,
    register,
    formState: { errors },
  } = useFormContext();
  const { staticFilters } = useReportsQuery();

  const shortcuts = useMemo(() => {
    return staticFilters
      ? staticFilters.filterDate.map((filter) => {
          return { label: filter.label, value: filter.value, getValue: dateGetValueMap.get(filter.value) };
        })
      : [];
  }, [staticFilters]);

  return (
    <>
      <InputLabel htmlFor="reporting-date-range">Date</InputLabel>
      <Controller
        control={control}
        name="date"
        defaultValue={[null, null]}
        render={({ field: { onChange, value } }) => {
          return (
            <DateRangePicker
              value={value}
              sx={{ width: '258px' }}
              className={`date-range-picker ${!!errors.date ? 'hasError' : ''}`}
              shortcuts={shortcuts}
              error={!!errors.date}
              helperText={errors.date?.message as string}
              slotProps={{
                textField: {
                  id: 'reporting-date-range',
                  inputProps: { 'data-testid': 'reporting-date-range' },
                },
              }}
              hasReset={true}
              onChange={(newValue, ctx: IPickerChangeHandlerContext<DateRangeValidationError>) => {
                onChange(newValue);
                ctx.shortcut ? setValue('filterDate', ctx.shortcut.value) : setValue('filterDate', '');
              }}
            />
          );
        }}
      />
      <input type="hidden" {...register('filterDate')} />
    </>
  );
};

const dateGetValueMap = new Map([
  [
    'today',
    () => {
      const today = new Date();

      return [today, today];
    },
  ],
  [
    'current_week',
    () => {
      const today = new Date();

      return [startOfISOWeek(today), endOfISOWeek(today)];
    },
  ],
  [
    'last_week',
    () => {
      const today = new Date();

      return [startOfISOWeek(subDays(today, 7)), endOfISOWeek(subDays(today, 7))];
    },
  ],
  [
    'current_month',
    () => {
      const today = new Date();

      return [startOfMonth(today), endOfMonth(today)];
    },
  ],
  [
    'last_month',
    () => {
      const today = new Date();

      return [startOfMonth(subMonths(today, 1)), endOfMonth(subMonths(today, 1))];
    },
  ],
]);
